import "./html-loader-element.ts"
import type { paths } from "@lightmill/log-api"
import createClient, { type Client } from "openapi-fetch"
import commonStyleUrl from "../common.css?url"
import { getElement } from "../lib/get-elements.ts"
import { html, minDigits } from "../lib/utils.ts"
import { WithAttributeProps, string } from "../mixins/with-attributes-props.ts"
import { HTMLHardConfirmElement } from "./html-hard-confirm-element.ts"
import "iconify-icon"

const mainTemplate = html`
	<style>
		@import url(${commonStyleUrl});
		:host {
			--running-color: var(--blue-300);
			--completed-color: var(--green-300);
			--pending-color: var(--gray-500);
			--interrupted-color: var(--red-300);
			--canceled-color: var(--red-300);
		}
		:host([hidden]) {
			display: none;
		}
		.timelines {
			border-style: none;
			border-collapse: collapse;
			width: 100%;
			text-align: left;
		}
		.timeline td {
			padding: var(--space-s) var(--space-m);
		}
		.actions {
			display: flex;
			justify-content: flex-start;
			align-items: center;
			gap: var(--space-s);

			iconify-icon {
				vertical-align: bottom;
			}
		}
		.action {
			visibility: hidden;
		}
		.action {
			display: none;
			white-space: nowrap;
		}
		.action.test {
			display: unset;
		}
		.status-value {
			display: none;
			white-space: nowrap;
		}
		.timeline[data-status="pending"] {
			.status {
				color: var(--pending-color);
			}
			.pending {
				display: unset;
			}
			.action.start {
				display: unset;
			}
		}
		.timeline[data-status="running"] {
			.status {
				color: var(--running-color);
			}
			.running {
				display: unset;
			}
			.action.resume {
				display: unset;
			}
		}
		.timeline:not([data-status="canceled"]):not([data-status="pending"]) .action.cancel {
				display: unset;
		}
		.timeline[data-status="completed"] {
			.status {
				color: var(--completed-color);
			}
			.completed {
				display: unset;
			}
		}
		.timeline[data-status="canceled"] {
			.status {
				color: var(--canceled-color);
			}
			.canceled {
				display: unset;
			}
			.action.start {
				display: unset;
			}
		}
		.timeline[data-status="interrupted"] {
			.status {
				color: var(--interrupted-color);
			}
			.interrupted {
				display: unset;
			}
			.action.resume {
				display: unset;
			}
		}
		.timeline-label-test-icon {
			display: none;
		}
		.timeline[data-type="test"] {
			& .label {
				font-style: italic;
			}
			& .timeline-label-test-icon {
				display: unset;
			}
		}
		.error {
			color: var(--interrupted-color);
		}
		.timeline:not([data-timeline-id]) {
			.action.resume, .action.start, .action.test {
				display: none;
			}
		}
		.label {
			max-width: 20ch;
			text-overflow: ellipsis;
			white-space: nowrap;
			overflow: hidden;
		}
		.timeline:hover {
			background-color: var(--gray-800);
			.action {
				visibility: visible;
			}
			.label {
				white-space: normal;
				overflow: visible;
			}
		}
		.actions-container {
			position: relative;
			width: fit-content;
		}
		.loader {
			display: none;
			position: absolute;
			color: var(--gray-500);
			height: 100%;
			padding: var(--space-s) var(--space-m);
			left: 0;
			top: 0;
			box-sizing: border-box;
			justify-content: center;
			align-items: center;
		}
		.timeline.loading {
			.loader {
				display: flex;
			}
			.action {
				visibility: hidden;
			}
		}
		.loading-message x-loader {
			vertical-align: bottom;
		}
		:host(:not([start-src])) {
			.action.start {
				display: none;
			}
			.action.test {
				display: none;
			}
		}
	</style>
	<div class="main"><p class="loading-message"><x-loader></x-loader>&nbsp;Loading...</p></div>
	<x-hard-confirm class="confirm"></x-hard-confirm>
`

const timelineTemplate = html`
	<tr class="timeline">
		<td class="label">
			<span aria-hidden="true" class="timeline-label-test-icon"><iconify-icon icon="tabler:test-pipe-2-filled" inline></iconify-icon>&nbsp;</span><span class="content"></span>
		</td>
		<td class="status">
			<span class="status-value pending">
				<iconify-icon icon="tabler:clock-hour-4-filled" inline></iconify-icon> pending
			</span>
			<span class="status-value running">
				<iconify-icon icon="tabler:player-play-filled" inline></iconify-icon> running
			</span>
			<span class="status-value completed">
				<iconify-icon icon="tabler:circle-check-filled" inline></iconify-icon> completed
			</span>
			<span class="status-value interrupted">
				<iconify-icon icon="tabler:player-pause-filled" inline></iconify-icon> interrupted
			</span>
			<span class="status-value canceled">
				<iconify-icon icon="tabler:circle-x-filled" inline></iconify-icon> canceled
			</span>
		</td>
		<td class="actions-container">
			<div class="actions">
				<a class="test action button">
					<iconify-icon icon="tabler:test-pipe-2-filled" width="1.2em" height="1.2em"></iconify-icon> test
				</a>
				<a class="start action button">
					<iconify-icon icon="tabler:player-play-filled" width="1.2em" height="1.2em"></iconify-icon> start
				</a>
				<a class="resume action button">
					<iconify-icon icon="tabler:player-play-filled" width="1.2em" height="1.2em"></iconify-icon> resume
				</a>
				<button class="cancel action button">
					<iconify-icon icon="tabler:trash-x-filled" width="1.2em" height="1.2em"></iconify-icon> cancel
				</button>
			</div>
			<div class="loader">
				<x-loader></x-loader>
			</div>
		</td>
	</tr>
`

export class HTMLTimelineAdminElement extends WithAttributeProps(HTMLElement, {
	logSrc: string(),
	timelinesSrc: string(),
	startSrc: string(),
}) {
	#shadowRoot = this.attachShadow({ mode: "closed" })
	#client: Client<paths> | null = null

	connectedCallback() {
		this.#client =
			this.logSrc == null ? null : createClient({ baseUrl: this.logSrc })
		this.#update()
	}

	static observedAttributes = ["log-src", "timelines-src"] as const
	attributeChangedCallback(
		name: (typeof HTMLTimelineAdminElement)["observedAttributes"][number],
	) {
		if (name === "log-src") {
			this.#client =
				this.logSrc == null ? null : createClient({ baseUrl: this.logSrc })
		}
		this.#update()
	}

	#cancelLoading = () => {}
	async #getMappedTimelines() {
		let thisCancelLoading = () => {}
		try {
			this.#cancelLoading()
			let isCanceled = false
			let abortController = new AbortController()
			thisCancelLoading = () => {
				isCanceled = true
				abortController.abort()
				this.#cancelLoading = () => {}
			}
			this.#cancelLoading = thisCancelLoading
			if (this.#client == null || this.timelinesSrc == null) {
				return null
			}
			const [timelineResponse, runResponse] = await Promise.all([
				fetch(`${this.timelinesSrc}`, { signal: abortController.signal }),
				this.#client.GET("/runs", {
					signal: abortController.signal,
					params: {
						query: {
							"filter[status]": ["completed", "idle", "interrupted", "running"],
						},
					},
					cache: "no-cache",
				}),
			])
			if (isCanceled) {
				throw new CanceledError()
			}
			if (timelineResponse.status !== 200) {
				throw new LoadingError(
					`Failed to fetch timeline: ${timelineResponse.statusText}`,
				)
			}
			if (runResponse.error != null) {
				throw new LoadingError(
					`Failed to fetch runs: ${runResponse.error.errors.map((e) => e.code).join(", ")}`,
				)
			}
			const timelines = await timelineResponse
				.json()
				.then((content) => content.data as Array<{ id: string; link: string }>)
			if (isCanceled) {
				throw new CanceledError()
			}
			const runMap = new Map(
				runResponse.data.data.map((run) => [run.attributes.name, run]),
			)
			const result: Array<{
				status: string
				timelineId: string | null
				runId: string | null
				runName: string | null
				link: string | null
			}> = timelines.map((timeline) => {
				const run = runMap.get(timeline.id)
				runMap.delete(timeline.id)
				if (run == null) {
					return {
						timelineId: timeline.id,
						runId: null,
						runName: null,
						status: "pending" as const,
						link: timeline.link,
					}
				}
				return {
					timelineId: timeline.id,
					runId: run.id,
					runName: run.attributes.name ?? null,
					status: run.attributes.status,
					link: timeline.link,
				}
			})
			for (const run of runMap.values()) {
				result.push({
					timelineId: null,
					runId: run.id,
					runName: run.attributes.name ?? null,
					status: run.attributes.status,
					link: null,
				})
			}
			return result
		} finally {
			if (this.#cancelLoading === thisCancelLoading) {
				this.#cancelLoading = () => {}
			}
		}
	}

	async #update() {
		this.#shadowRoot.innerHTML = ""
		this.#shadowRoot.appendChild(mainTemplate.content.cloneNode(true))
		let main = this.#shadowRoot.querySelector(".main")
		if (main == null) {
			throw new Error("Could not find main element")
		}
		try {
			const timelines = await this.#getMappedTimelines()
			main.innerHTML = ""
			if (timelines == null) return
			const table = document.createElement("table")
			table.classList.add("timelines")
			for (const timelineData of timelines) {
				table.appendChild(this.#createTimelineRow(timelineData))
			}
			main.appendChild(table)
			this.dispatchEvent(new CustomEvent("load"))
		} catch (e) {
			if (
				e instanceof CanceledError ||
				(e instanceof DOMException && e.name === "AbortError")
			) {
				return
			}
			let errorToShow: string | null = null
			if (e instanceof Error) {
				let showError = this.dispatchEvent(
					new ErrorEvent("error", {
						error: e,
						cancelable: true,
					}),
				)
				if (!showError) return
				errorToShow = e.message
			} else {
				let showError = this.dispatchEvent(
					new ErrorEvent("error", {
						error: new Error(String(e)),
						cancelable: true,
					}),
				)
				if (!showError) return
				errorToShow = String(e)
			}
			// biome-ignore lint/suspicious/noConsole: This is intentional.
			console.error(e)
			this.#showError(errorToShow)
		}
	}

	#showError(message: string) {
		let main = this.#shadowRoot.querySelector(".main")
		if (main == null) {
			main = document.createElement("div")
			main.classList.add("main")
			this.#shadowRoot.innerHTML = ""
			this.#shadowRoot.appendChild(main)
		}
		main.innerHTML = `<div class="error">${message}</div>`
	}

	static #testTimelineRegex = /^test-(?<id>[^-]+)-(?<date>.+)$/

	#createTimelineRow({
		timelineId,
		status,
		link,
		runId,
		runName,
	}: TimelineData) {
		const node = timelineTemplate.content.cloneNode(true)
		if (!(node instanceof DocumentFragment)) {
			throw new Error("Could not find timeline element")
		}
		const timelineElement = getElement(".timeline", node, HTMLTableRowElement)
		const labelElement = getElement(".label", node, HTMLTableCellElement)
		const labelContentElement = getElement(".label .content", node, HTMLElement)
		const startElement = getElement(".start.action", node, HTMLAnchorElement)
		const testElement = getElement(".test.action", node, HTMLAnchorElement)
		const resumeElement = getElement(".resume.action", node, HTMLAnchorElement)
		const cancelElement = getElement(".cancel.action", node, HTMLButtonElement)
		const confirm = getElement(
			".confirm",
			this.#shadowRoot,
			HTMLHardConfirmElement,
		)
		timelineElement.dataset.status = status
		if (timelineId != null) {
			timelineElement.dataset.timelineId = timelineId
		}
		let label = runName ?? timelineId ?? "Unknown"
		const testMatch = label.match(HTMLTimelineAdminElement.#testTimelineRegex)
		if (testMatch != null && testMatch.groups != null) {
			let date = new Date(testMatch.groups.date)
			label = `${testMatch.groups.id} test`
			labelContentElement.textContent = label
			labelElement.title = `${label} from ${formatDate(date)} at ${formatTime(date)}`
			timelineElement.dataset.type = "test"
		} else {
			labelContentElement.textContent = label
			timelineElement.dataset.type = "run"
		}
		cancelElement.addEventListener("click", async (event) => {
			event.preventDefault()
			if (timelineId == null) {
				this.#cancelTimeline({ runId, timelineElement, timelineId })
				return
			}

			confirm.innerHTML = `
				Are you sure you want to cancel ${timelineId}? This action cannot be undone.
				<span slot="confirm"><iconify-icon width="1.2em" height="1.2em" icon="tabler:trash-x-filled" inline></iconify-icon>&nbsp;Cancel ${timelineId}</span>
				<span slot="cancel"><iconify-icon width="1.2em" height="1.2em" icon="tabler:run" inline></iconify-icon>&nbsp;Keep ${timelineId}</span>
			`
			confirm.code = `cancel ${timelineId}`
			confirm.addEventListener(
				"confirm",
				() => {
					this.#cancelTimeline({ runId, timelineElement, timelineId })
				},
				{ once: true },
			)
			confirm.show()
		})
		if (timelineId == null || link == null) {
			return timelineElement
		}
		const startUrl = new URL(
			`${window.location.origin}${this.startSrc?.startsWith("/") ? "" : "/"}${this.startSrc}`,
		)
		startUrl.searchParams.set("timeline-url", link)
		startUrl.searchParams.set("timeline-id", timelineId)
		startElement.href = startUrl.toString()
		const testUrl = new URL(startUrl)
		testUrl.searchParams.set("test", "true")
		testElement.href = testUrl.toString()
		const resumeUrl = new URL(startUrl)
		resumeUrl.searchParams.set("resume", "true")
		if (runId != null) {
			resumeUrl.searchParams.set("run-id", runId)
		}
		resumeElement.href = resumeUrl.toString()
		return timelineElement
	}

	async #cancelTimeline({
		timelineElement,
		runId,
		timelineId,
	}: Pick<TimelineData, "runId" | "timelineId"> & {
		timelineElement: HTMLTableRowElement
	}) {
		if (this.#client == null) {
			let message = "Cannot cancel timeline: client not initialized"
			this.#showError(message)
			this.dispatchEvent(new ErrorEvent("error", { error: new Error(message) }))
			return
		}
		if (runId == null) {
			let message = "Cannot cancel timeline: no run id"
			this.#showError(message)
			this.dispatchEvent(new ErrorEvent("error", { error: new Error(message) }))
			return
		}
		timelineElement.classList.add("loading")
		const response = await this.#client.PATCH("/runs/{id}", {
			headers: {
				"Content-Type": "application/vnd.api+json",
			},
			params: { path: { id: runId } },
			body: {
				data: {
					type: "runs",
					id: runId,
					attributes: { status: "canceled" },
				},
			},
		})
		if (response.error != null) {
			let message = `Failed to cancel timeline: ${response.error.errors.map((e) => e.code).join(", ")}`
			this.#showError(message)
			this.dispatchEvent(new ErrorEvent("error", { error: new Error(message) }))
			return
		}
		timelineElement.classList.remove("loading")
		if (timelineId) {
			timelineElement.dataset.status = "pending"
		} else {
			timelineElement.remove()
		}
	}

	reload() {
		this.#update()
	}
}

customElements.define("x-timeline-admin", HTMLTimelineAdminElement)

class CanceledError extends Error {
	constructor({ cause }: { cause?: Error } = {}) {
		super("Canceled", { cause })
	}
}

export class LoadingError extends Error {
	constructor(message: string, { cause }: { cause?: Error } = {}) {
		super(message, { cause })
	}
}

interface TimelineData {
	timelineId: string | null
	status: string
	link: string | null
	runName: string | null
	runId: string | null
}

function formatDate(date: Date) {
	return `${date.getFullYear()}-${minDigits(date.getMonth(), 2)}-${minDigits(
		date.getDate(),
		2,
	)}`
}

function formatTime(date: Date) {
	return `${minDigits(date.getHours(), 2)}:${minDigits(
		date.getMinutes(),
		2,
	)}:${minDigits(date.getSeconds(), 2)}`
}
